import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Camera, Sparkles, ArrowRight, X, Loader2 } from 'lucide-react';

export const STEPS = {
  fr: ['Envoi de votre photo...', 'Analyse de votre photo...', 'G\u00e9n\u00e9ration du prompt de simulation...', 'Cr\u00e9ation de votre transformation IA...', 'Finalisation et sauvegarde...'],
  en: ['Uploading your photo...', 'Analyzing your photo...', 'Generating simulation prompt...', 'Creating your AI transformation...', 'Finalizing and saving...'],
};

const DropZone = ({ language, fileInputRef, onFileSelect }) => (
  <div
    onClick={() => fileInputRef.current?.click()}
    className="border-2 border-dashed border-white/15 rounded-2xl p-12 text-center cursor-pointer hover:border-lux-gold/40 hover:bg-white/[0.02] transition-colors duration-300"
    data-testid="simulation-upload-zone"
  >
    <div className="p-4 rounded-2xl bg-lux-gold/10 inline-block mb-4">
      <Camera className="w-8 h-8 text-[var(--lux-gold)]" />
    </div>
    <h3 className="text-white font-semibold mb-2">{language === 'fr' ? 'Choisissez votre photo' : 'Choose your photo'}</h3>
    <p className="text-white/40 text-sm max-w-md mx-auto">
      {language === 'fr'
        ? 'Photo de face, bien \u00e9clair\u00e9e, avec un fond neutre pour de meilleurs r\u00e9sultats. JPEG, PNG ou WebP (max 10MB)'
        : 'Front-facing, well-lit photo with a neutral background for best results. JPEG, PNG or WebP (max 10MB)'}
    </p>
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      onChange={(e) => { onFileSelect(e.target.files?.[0]); e.target.value = ''; }}
      className="hidden"
      data-testid="simulation-file-input"
    />
  </div>
);

const ProgressPanel = ({ language, progressStep }) => {
  const steps = STEPS[language === 'fr' ? 'fr' : 'en'];
  const percent = ((progressStep + 1) / steps.length) * 100;
  return (
    <div className="space-y-4 py-4" data-testid="simulation-progress">
      <Progress value={percent} className="h-2 bg-white/5" />
      <div className="flex items-center justify-center gap-3">
        <Loader2 className="w-5 h-5 text-[var(--lux-gold)] animate-spin" />
        <p className="text-[var(--lux-amber)] text-sm font-medium" data-testid="simulation-progress-step">{steps[Math.min(progressStep, steps.length - 1)]}</p>
      </div>
      <p className="text-white/30 text-xs text-center">
        {language === 'fr' ? 'Cela peut prendre 1 \u00e0 2 minutes, vous pouvez patienter ici...' : 'This may take 1 to 2 minutes, you can wait here...'}
      </p>
    </div>
  );
};

export const UploadCard = ({ language, preview, loading, progressStep, fileInputRef, onFileSelect, onReset, onStart }) => (
  <Card className="bg-lux-panel/90 border-white/[0.08] fade-in" data-testid="simulation-upload-card">
    <CardContent className="pt-6">
      {!preview ? (
        <DropZone language={language} fileInputRef={fileInputRef} onFileSelect={onFileSelect} />
      ) : (
        <div className="space-y-4">
          <div className="relative max-w-sm mx-auto">
            <img src={preview} alt="Preview" className="w-full rounded-xl border border-white/10" />
            <button
              onClick={onReset}
              data-testid="simulation-remove-photo-button"
              aria-label="Remove photo"
              className="absolute top-2 right-2 p-2 rounded-lg bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm text-xs text-white/70">
              {language === 'fr' ? 'Photo originale' : 'Original photo'}
            </div>
          </div>

          {loading ? (
            <ProgressPanel language={language} progressStep={progressStep} />
          ) : (
            <div className="flex justify-center">
              <Button
                data-testid="simulation-start-button"
                onClick={onStart}
                className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] font-semibold px-8 h-12 text-base active:scale-[0.98] transition-[background-color,transform] duration-200"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {language === 'fr' ? 'Lancer la simulation' : 'Start simulation'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);
